import React from "react";
import * as Mui from "@mui/material";
import {
  ApprovalRounded,
  CalendarMonthRounded,
  CheckCircleRounded,
  DescriptionRounded,
  Groups2Rounded,
  HolidayVillageRounded,
  PersonOffRounded,
} from "@mui/icons-material";
import { StatCard } from "./homeUi";

// counts is the get-overall-attendance-count response (see
// TeamZone/Attendance/AttendanceStatus/index.js for the same call) — it only
// covers Attendance/Leave Summary/Shift headcounts, not Approve/EOD Report,
// so those two cards stay on placeholder values until their own endpoints
// are wired in.
//
// navigationTarget.search reuses the same url-driven filters
// Attendance/index.js already parses (see its urlDrivenFilters memo and
// AttendanceExceptionsSection's identical convention) rather than inventing
// a new query scheme: workforce=total drives the Total/Planned/Week
// Off/Leave toggle on Attendance's Overview tab, component=employee-view +
// attendance=present/absent opens the employee list pre-filtered. Week
// Off/Pending Approvals/Pending Reports have no such deep link, so they just
// land on their module's page root, same as this card row's sibling
// sections' own CTA buttons (ShiftCoverageSection's "View Full Roster",
// PendingApprovalsSection's/PendingReportsSection's footer buttons).
const buildModuleCardRegistry = (counts, dayLabel, approvalCategoryCounts) => [
  {
    module: "Attendance",
    title: "Total Employees",
    value: String(counts?.overall_count ?? 0),
    detail: "",
    icon: Groups2Rounded,
    accent: "#16A34A",
    tint: "#EAFBF1",
    navigationTarget: { moduleName: "Attendance", search: "component=overview&workforce=total" },
  },
  {
    module: "Attendance",
    title: `Present ${dayLabel}`,
    requiresEmployeeView: true,
    value: String(counts?.present_count ?? 0),
    detail: "",
    icon: CheckCircleRounded,
    accent: "#16A34A",
    tint: "#ECFDF3",
    navigationTarget: {
      moduleName: "Attendance",
      search: "component=employee-view&attendance=present",
    },
  },
  {
    module: "Attendance",
    title: `Absent ${dayLabel}`,
    requiresEmployeeView: true,
    value: String(counts?.absent_count ?? 0),
    detail: "",
    icon: PersonOffRounded,
    accent: "#EF4444",
    tint: "#FEF2F2",
    navigationTarget: {
      moduleName: "Attendance",
      search: "component=employee-view&attendance=absent",
    },
  },
  {
    module: "Attendance",
    title: `On Leave ${dayLabel}`,
    requiresEmployeeView: true,
    value: String(counts?.leave_count ?? 0),
    detail: "",
    icon: HolidayVillageRounded,
    accent: "#7C3AED",
    tint: "#F5F3FF",
    navigationTarget: {
      moduleName: "Attendance",
      search: "component=employee-view&workforce=leave",
    },
  },
  {
    module: "Rosters",
    title: `Week Off ${dayLabel}`,
    value: String(counts?.weekoff_count ?? 0),
    detail: "",
    icon: CalendarMonthRounded,
    accent: "#64748B",
    tint: "#F8FAFC",
    // Was the only module-linked card here with no `search` param at all --
    // harmless only because Roster's own default view happens to be Daily
    // today. Made explicit so this still lands on the "Total Employees"
    // Daily view even if that default ever changes.
    navigationTarget: { moduleName: "Rosters", search: "view=daily&status=weeklyOff" },
  },
  {
    module: "Approvals",
    title: "Pending Approvals",
    value: String(
      approvalCategoryCounts?.overall_pending_counts ??
        counts?.pending_approval_counts ??
        0,
    ),
    detail: "",
    icon: ApprovalRounded,
    accent: "#F97316",
    tint: "#FFF7ED",
    navigationTarget: { moduleName: "Approvals" },
  },
  {
    module: "EOD Report",
    title: "Pending Reports",
    value: String(counts?.pending_reports_counts ?? 0),
    detail: "",
    icon: DescriptionRounded,
    accent: "#2563EB",
    tint: "#EFF6FF",
    navigationTarget: { moduleName: "EOD Report" },
  },
];

export const WorkforceSummaryCards = ({
  availableModules,
  canViewEmployeeAttendance = false,
  approvalAccess = {},
  attendanceCounts,
  approvalCategoryCounts,
  dayLabel = "Today",
  loading = false,
  navigateToModule,
}) => {
  const visibleSummaryCards = buildModuleCardRegistry(
    attendanceCounts,
    dayLabel,
    approvalCategoryCounts,
  )
    .filter((card) => availableModules.has(card.module) &&
      (!card.requiresEmployeeView || canViewEmployeeAttendance) &&
      (card.module !== "Approvals" || approvalAccess.all))
    .map((card) => ({
      ...card,
      onClick: () =>
        navigateToModule(card.navigationTarget.moduleName, card.navigationTarget.search),
    }));

  if (visibleSummaryCards.length === 0) return null;

  return (
    <Mui.Box
      sx={{
        display: "grid",
        // auto-fit + minmax sizes columns off this row's own rendered
        // width, not a window-width breakpoint — a fixed `lg: nowrap`
        // switch has no idea the sidebar (240px expanded, 65px collapsed)
        // already ate part of that width, so on an ordinary 1366–1440px
        // laptop window (comfortably past the 1200px `lg` threshold) it
        // forced all 7 cards onto one row anyway, squeezing each one
        // narrower than its title text and wrapping "Total Employees" /
        // "Pending Approvals" / "Pending Reports" onto two lines. This
        // wraps to a new row on its own once a card would drop below
        // 180px, whatever the sidebar or window is doing.
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 2,
      }}
    >
      {loading
        ? visibleSummaryCards.map((card) => (
            <Mui.Box
              key={card.title}
              sx={{
                border: "1px solid #E2E8F0",
                borderRadius: 3,
                p: 1.75,
                backgroundColor: "#fff",
              }}
            >
              <Mui.Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Mui.Box sx={{ flex: 1, minWidth: 0 }}>
                  <Mui.Skeleton variant="text" width="60%" height={22} />
                  <Mui.Skeleton variant="text" width="36%" height={40} />
                </Mui.Box>
                <Mui.Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: 2 }} />
              </Mui.Stack>
            </Mui.Box>
          ))
        : visibleSummaryCards.map((card) => <StatCard key={card.title} {...card} />)}
    </Mui.Box>
  );
};
